<template>
  <div class="product-lookup">
    <PageHeader
      title="상품 승인"
      description="수집 여부를 결정하세요."
      :margin-bottom="true"
    />

    <section class="lookup-toolbar">
      <div class="filter-item filter-item--wide">
        <label class="filter-label">마켓 입력</label>
        <el-input
          v-model="filters.marketUrl"
          placeholder="마켓 입력"
          clearable
          @keyup.enter="handleSearch"
        />
      </div>
      <div class="filter-item">
        <label class="filter-label">그룹</label>
        <el-select
          v-model="filters.groupid"
          placeholder="전체"
          clearable
          :disabled="filters.deleted"
        >
          <el-option
            v-for="group in groupOptions"
            :key="group.groupid"
            :label="groupLabel(group)"
            :value="group.groupid"
          />
        </el-select>
      </div>
      <div class="filter-item">
        <label class="filter-label">마켓 상태</label>
        <el-select
          v-model="filters.marketStatus"
          placeholder="전체"
          :disabled="filters.deleted"
        >
          <el-option
            v-for="option in marketStatusOptions"
            :key="option.value"
            :label="option.label"
            :value="option.value"
          />
        </el-select>
      </div>
      <div class="filter-item filter-item--switch">
        <label class="filter-label">삭제된 마켓</label>
        <el-switch v-model="filters.deleted" />
      </div>
      <div class="toolbar-spacer" />
      <div class="toolbar-actions">
        <el-button type="primary" :icon="Search" @click="handleSearch">
          검색
        </el-button>
        <el-button plain @click="toggleOrder">{{ orderLabel }}</el-button>
        <el-button plain @click="cycleSortMode">{{ sortModeLabel }}</el-button>
        <el-button plain :disabled="!products.length" @click="handleBulkReject">
          {{ allBanned ? '전체 금지 해제' : '전체 금지' }}
        </el-button>
        <el-button
          type="success"
          :disabled="!products.length"
          @click="handleConfirm"
        >
          {{ hasSelection ? '선택 승인' : '전체 승인' }}
        </el-button>
      </div>
    </section>

    <section class="lookup-body">
      <AppLoading v-if="loading" text="상품을 불러오는 중입니다..." />
      <template v-else>
        <div v-if="products.length" class="products-grid">
          <div
            v-for="product in products"
            :key="product.productid"
            :class="[
              'product-card',
              { 'banned-card': product.isBanned, 'selected-card': isSelected(product.productid) }
            ]"
            @click="handleCardClick(product, $event)"
          >
            <div class="card-checkbox" @click.stop>
              <el-checkbox
                :model-value="isSelected(product.productid)"
                @change="value => handleSelect(product.productid, value)"
              />
            </div>
      <div class="product-image">
        <img
          :src="product.thumbnail || '/no-image.jpg'"
          :alt="product.title || '상품 이미지'"
          @error="handleImageError"
          draggable="false"
        />
      </div>
            <div class="product-info">
              <h4 class="product-title" :title="product.title">
                {{ product.title || '-' }}
              </h4>
              <div class="product-details">
                <span class="product-price">{{ formatPrice(product.price) }}</span>
                <span v-if="product.interestedCustomerCount" class="product-chip">
                  관심 {{ formatNumber(product.interestedCustomerCount) }}
                </span>
                <span v-if="product.bestCount" class="product-chip warn">
                  BEST {{ formatNumber(product.bestCount) }}
                </span>
              </div>
      <div class="product-meta">
        <div class="product-market-copy" @click.stop="copyMarketUrl(product.marketSlug)">
          <span class="product-market">{{ product.marketSlug || '-' }}</span>
          <img
            src="@/assets/icon_content_copy.svg"
            alt="복사"
            class="copy-icon"
            title="클릭하여 복사"
          />
        </div>
        <span class="product-date">수집일: {{ formatDate(product.createdAt) }}</span>
      </div>
              <p v-if="product.bannedWords?.length" class="banned-list">
                금지어: {{ product.bannedWords.join(', ') }}
              </p>
              <div class="product-actions">
                <a
                  v-if="product.productUrl"
                  :href="product.productUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="product-link"
                  @click.stop
                >
                  상품 링크 →
                </a>
              </div>
            </div>
            <div v-if="product.isBanned" class="ban-overlay">
              <div class="ban-content">
                <el-icon class="ban-icon" :size="48">
                  <Remove />
                </el-icon>
                <div class="ban-label">금지됨</div>
                <div v-if="bannedWordsText(product)" class="ban-words">
                  {{ bannedWordsText(product) }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <el-empty v-else :description="emptyText" />
      </template>
    </section>
    <div class="lookup-pagination" :class="{ 'has-pagination': pagination.total > 0 }">
      <div class="pagination-right" v-if="pagination.total > 0">
        <el-pagination
          layout="total, sizes, prev, pager, next, jumper"
          :page-sizes="limitOptions"
          :total="pagination.total"
          v-model:current-page="pagination.page"
          :page-size="pagination.limit"
          @current-change="handlePageChange"
          @size-change="handleSizeChange"
        />
      </div>
    </div>
    <el-dialog
      v-model="optionModalVisible"
      title="승인 옵션"
      width="520px"
      align-center
      destroy-on-close
      :close-on-click-modal="false"
    >
      <div class="option-modal">
        <div class="option-summary">
          <span>승인 {{ pendingSummary.approve }}건</span>
          <span>금지 {{ pendingSummary.reject }}건</span>
        </div>
        <div class="option-list">
          <div v-for="option in statusOptionItems" :key="option.key" class="option-row">
            <div class="option-info">
              <div class="option-title">{{ option.label }}</div>
              <div class="option-desc">{{ option.description }}</div>
            </div>
            <el-switch v-model="optionFlags[option.key]" />
          </div>
        </div>
      </div>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="handleOptionCancel">취소</el-button>
          <el-button type="primary" :loading="loading" @click="handleOptionSubmit">
            옵션 적용 후 전송
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { ElMessage } from 'element-plus';
import { Search, Remove } from '@element-plus/icons-vue';
import PageHeader from '@/components/common/header.vue';
import AppLoading from '@/components/common/loading.vue';
import {
  fetchGroupCodes,
  fetchPendingProductList,
  updatePendingProductStatus
} from '@/services/n2n';

export default {
  name: 'N2nProductLookup',
  components: {
    PageHeader,
    AppLoading
  },
  setup() {
    const loading = ref(false);
    const products = ref([]);
    const groupOptions = ref([]);
    const selectedProductIds = ref([]);
    const optionModalVisible = ref(false);
    const sortMode = ref('default'); // default | banned | price
    const pendingStatus = ref({
      approveIds: [],
      rejectIds: []
    });
    const optionFlags = reactive({
      removeOptionName: true,
      shuffleTitle: false,
      translateImages: true,
      regenerateKeywords: false
    });
    const pagination = reactive({
      page: 1,
      limit: 60,
      total: 0
    });
    const filters = reactive({
      marketUrl: '',
      groupid: '',
      marketStatus: 'all',
      order: 'desc',
      deleted: false
    });
    const limitOptions = [20, 60, 100, 200, 400, 1000];
    const marketStatusOptions = [
      { label: '전체', value: 'all' },
      { label: '연결중', value: '연결중' },
      { label: '연결성공', value: '연결성공' },
      { label: '연결실패', value: '연결실패' },
      { label: '연결중지', value: '연결중지' },
      { label: '판매중지', value: '판매중지' }
    ];
    const statusOptionItems = [
      {
        key: 'removeOptionName',
        label: '옵션명 제거',
        description: '상품명에 포함된 옵션명을 제거해 셔플에 도움을 줍니다.'
      },
      {
        key: 'shuffleTitle',
        label: '제목 셔플',
        description: '상품명의 앞 4단어를 제외한 단어를 무작위로 셔플합니다.'
      },
      {
        key: 'translateImages',
        label: '이미지 번역',
        description: '번역되지 않은 중국어 이미지를 번역합니다.'
      },
      {
        key: 'regenerateKeywords',
        label: '키워드 재생성',
        description: '재생성시 카탈로그 매칭의 가능성이 감소합니다.'
      }
    ];
    const emptyText = computed(() =>
      loading.value ? '로딩 중...' : '조건에 맞는 상품이 없습니다.'
    );
    const selectedProducts = computed(() =>
      products.value.filter(product => selectedProductIds.value.includes(product.productid))
    );
    const pendingSummary = computed(() => ({
      approve: pendingStatus.value.approveIds.length,
      reject: pendingStatus.value.rejectIds.length
    }));
    const allBanned = computed(() =>
      products.value.length > 0 && products.value.every(product => product.isBanned)
    );
    const hasSelection = computed(() => selectedProductIds.value.length > 0);
    const sortModeLabel = computed(() => {
      if (sortMode.value === 'banned') return '금지어순';
      if (sortMode.value === 'price') return '가격순';
      return '기본순';
    });
    const orderLabel = computed(() => (filters.order === 'asc' ? '과거순' : '최신순'));

    let autoSearchTimer = null;
    let autoSearchSuspended = false;

    const clearSearchTimer = () => {
      if (autoSearchTimer) {
        clearTimeout(autoSearchTimer);
        autoSearchTimer = null;
      }
    };

    const scheduleSearch = (delay = 0) => {
      if (autoSearchSuspended) return;
      clearSearchTimer();
      autoSearchTimer = setTimeout(() => {
        pagination.page = 1;
        loadProducts();
      }, delay);
    };

    const groupLabel = group => {
      if (!group) return '-';
      return group.groupname ?? group.groupid ?? '-';
    };

    const buildQueryParams = () => {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        order: filters.order || 'desc'
      };
      if (filters.marketUrl.trim()) {
        params.market_url = filters.marketUrl.trim();
      }
      if (filters.groupid) {
        params.groupid = filters.groupid;
      }
      if (!filters.deleted && filters.marketStatus !== 'all') {
        params.market_status = filters.marketStatus;
      }
      if (filters.deleted) {
        params.deleted = true;
      }
      return params;
    };

    const syncSelections = () => {
      const currentIds = new Set(products.value.map(product => product.productid));
      selectedProductIds.value = selectedProductIds.value.filter(id => currentIds.has(id));
    };

    const setProductList = items => {
      products.value = items.map((item, index) => ({
        ...item,
        // 금지어가 있더라도 기본은 "금지"로 표시하지 않는다.
        // 사용자가 금지어 목록을 보고 직접 금지/해제를 결정한다.
        isBanned: false,
        baseIndex: index
      }));
      applySortMode();
      syncSelections();
    };

    const resetOptionFlags = () => {
      optionFlags.removeOptionName = true;
      optionFlags.shuffleTitle = false;
      optionFlags.translateImages = true;
      optionFlags.regenerateKeywords = false;
    };

    const resetPendingStatus = () => {
      pendingStatus.value = {
        approveIds: [],
        rejectIds: []
      };
    };

    const buildOptionPayload = () => ({
      removeOptionName: Boolean(optionFlags.removeOptionName),
      shuffleTitle: Boolean(optionFlags.shuffleTitle),
      translateImages: Boolean(optionFlags.translateImages),
      regenerateKeywords: Boolean(optionFlags.regenerateKeywords)
    });

    const loadProducts = async () => {
      loading.value = true;
      try {
        const params = buildQueryParams();
        const { data } = await fetchPendingProductList(params);
        const list = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
            ? data
            : data?.items || [];
        setProductList(list);
        const paginationInfo = data?.pagination;
        pagination.total = paginationInfo?.total ?? list.length;
      } catch (error) {
        products.value = [];
        pagination.total = 0;
        ElMessage.error(error.response?.data?.message || '상품 목록을 불러올 수 없습니다.');
      } finally {
        loading.value = false;
      }
    };

    const loadGroupOptions = async () => {
      try {
        const { data } = await fetchGroupCodes();
        if (data?.data) {
          groupOptions.value = data.data;
        } else if (Array.isArray(data)) {
          groupOptions.value = data;
        } else {
          groupOptions.value = data?.data ?? [];
        }
      } catch (error) {
        ElMessage.error(error.response?.data?.message || '그룹 정보를 불러오지 못했습니다.');
      }
    };

    const handleSearch = () => {
      clearSearchTimer();
      pagination.page = 1;
      loadProducts();
    };

    const handlePageChange = page => {
      pagination.page = page;
      loadProducts();
    };

    const handleSizeChange = size => {
      pagination.limit = size;
      pagination.page = 1;
      loadProducts();
    };

    const toggleBan = product => {
      if (!product) return;
      product.isBanned = !product.isBanned;
    };

    const handleCardClick = (product, event) => {
      if (!product) return;
      if (event?.target?.closest('.card-checkbox') || event?.target?.closest('.product-link')) {
        return;
      }
      toggleBan(product);
    };

    const handleSelect = (productid, checked) => {
      if (checked) {
        if (!selectedProductIds.value.includes(productid)) {
          selectedProductIds.value = [...selectedProductIds.value, productid];
        }
      } else {
        selectedProductIds.value = selectedProductIds.value.filter(id => id !== productid);
      }
    };

    const isSelected = productid => selectedProductIds.value.includes(productid);

    const formatNumber = value => {
      const num = Number(value);
      if (!Number.isFinite(num)) return value ?? '-';
      return num.toLocaleString();
    };

    const formatPrice = value => {
      if (value === null || value === undefined) return '-';
      const num = Number(value);
      if (!Number.isFinite(num)) return value;
      return `${num.toLocaleString()}원`;
    };

    const hasBannedWords = product => {
      const source = product?.bannedWords;
      if (Array.isArray(source)) return source.length > 0;
      if (source === null || source === undefined) return false;
      return String(source).trim().length > 0;
    };

    const stableSort = (list, compareFn) =>
      list
        .map((item, index) => ({ item, index }))
        .sort((a, b) => {
          const diff = compareFn(a.item, b.item);
          if (diff) return diff;
          return a.index - b.index;
        })
        .map(({ item }) => item);

    const getBaseIndex = product => {
      const num = Number(product?.baseIndex);
      return Number.isFinite(num) ? num : Number.POSITIVE_INFINITY;
    };

    const getPriceValue = product => {
      const raw = product?.price;
      const num = Number(raw);
      return Number.isFinite(num) ? num : Number.POSITIVE_INFINITY;
    };

    const applySortMode = () => {
      if (!products.value.length) return;
      if (sortMode.value === 'banned') {
        products.value = stableSort(
          products.value,
          (a, b) => Number(hasBannedWords(b)) - Number(hasBannedWords(a))
        );
        return;
      }
      if (sortMode.value === 'price') {
        products.value = stableSort(products.value, (a, b) => getPriceValue(b) - getPriceValue(a));
        return;
      }
      products.value = stableSort(products.value, (a, b) => getBaseIndex(a) - getBaseIndex(b));
    };

    const cycleSortMode = () => {
      if (sortMode.value === 'default') sortMode.value = 'banned';
      else if (sortMode.value === 'banned') sortMode.value = 'price';
      else sortMode.value = 'default';
      applySortMode();
    };

    const toggleOrder = () => {
      filters.order = filters.order === 'asc' ? 'desc' : 'asc';
    };

    const formatDate = value => {
      if (!value) return '-';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
      return date.toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    };

    const copyMarketUrl = async marketSlug => {
      if (!marketSlug) {
        ElMessage.warning('마켓명이 없습니다.');
        return;
      }
      const url = `https://smartstore.naver.com/${marketSlug}`;
      try {
        await navigator.clipboard.writeText(url);
        ElMessage.success('마켓 URL이 복사되었습니다.');
      } catch (error) {
        ElMessage.error('복사에 실패했습니다.');
      }
    };

    const bannedWordsText = product => {
      const source = product?.bannedWords;
      if (Array.isArray(source)) {
        return source.join(', ');
      }
      if (source) {
        return String(source);
      }
      return '';
    };

    const handleImageError = event => {
      event.target.src = '/no-image.jpg';
    };

    const submitStatusUpdate = async ({ approveIds = [], rejectIds = [], options = {} } = {}) => {
      const payload = { ...options };
      if (approveIds.length) {
        payload.approve = approveIds;
      }
      if (rejectIds.length) {
        payload.reject = rejectIds;
      }
      if (!payload.approve && !payload.reject) {
        ElMessage.warning('변경할 상품이 없습니다.');
        return;
      }
      try {
        loading.value = true;
        const { data } = await updatePendingProductStatus(payload);
        ElMessage.success(data?.message || '상품 상태를 갱신했습니다.');
        selectedProductIds.value = [];
        await loadProducts();
      } catch (error) {
        ElMessage.error(error.response?.data?.message || '상품 상태 갱신에 실패했습니다.');
      } finally {
        loading.value = false;
      }
    };

    const handleConfirm = () => {
      const approveIds = [];
      const rejectIds = [];
      const targetList = hasSelection.value ? selectedProducts.value : products.value;
      if (!targetList.length) {
        ElMessage.warning('승인할 상품이 없습니다.');
        return;
      }
      targetList.forEach(product => {
        if (product.isBanned) {
          rejectIds.push(product.productid);
        } else {
          approveIds.push(product.productid);
        }
      });
      pendingStatus.value = {
        approveIds,
        rejectIds
      };
      resetOptionFlags();
      optionModalVisible.value = true;
    };

    const handleOptionSubmit = async () => {
      const { approveIds, rejectIds } = pendingStatus.value;
      if (!approveIds.length && !rejectIds.length) {
        ElMessage.warning('승인할 상품이 없습니다.');
        return;
      }
      optionModalVisible.value = false;
      await submitStatusUpdate({
        approveIds,
        rejectIds,
        options: buildOptionPayload()
      });
      resetOptionFlags();
      resetPendingStatus();
    };

    const handleOptionCancel = () => {
      optionModalVisible.value = false;
      resetOptionFlags();
      resetPendingStatus();
    };

    const handleBulkReject = () => {
      if (!products.value.length) {
        ElMessage.warning('금지할 상품이 없습니다.');
        return;
      }
      const nextState = !allBanned.value;
      products.value.forEach(product => {
        product.isBanned = nextState;
      });
      ElMessage.success(nextState ? '모든 상품을 금지 상태로 표시했습니다.' : '모든 상품의 금지 상태를 해제했습니다.');
    };

    watch(
      () => filters.groupid,
      () => scheduleSearch()
    );

    watch(
      () => filters.order,
      () => scheduleSearch()
    );

    watch(
      () => filters.marketStatus,
      () => scheduleSearch()
    );

    watch(
      () => filters.deleted,
      value => {
        if (value && filters.groupid) {
          autoSearchSuspended = true;
          filters.groupid = '';
          autoSearchSuspended = false;
        }
        scheduleSearch();
      }
    );

    onMounted(async () => {
      await loadGroupOptions();
      await loadProducts();
    });

    return {
      Search,
      Remove,
      loading,
      filters,
      products,
      pagination,
      groupOptions,
      selectedProductIds,
      limitOptions,
      marketStatusOptions,
      statusOptionItems,
      optionFlags,
      optionModalVisible,
      pendingSummary,
      hasSelection,
      emptyText,
      groupLabel,
      formatNumber,
      formatPrice,
      formatDate,
      bannedWordsText,
      copyMarketUrl,
      orderLabel,
      sortModeLabel,
      cycleSortMode,
      toggleOrder,
      toggleBan,
      handleCardClick,
      handleSelect,
      handleSearch,
      handlePageChange,
      handleSizeChange,
      isSelected,
      handleConfirm,
      handleOptionSubmit,
      handleOptionCancel,
      handleBulkReject,
      handleImageError
    };
  }
};
</script>

<style scoped>
.product-lookup {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: var(--el-bg-color-page);
}

.lookup-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-sm);
  background-color: var(--el-bg-color);
  border: 1px solid var(--el-border-color-lighter);
  border-radius: var(--el-border-radius-base);
  padding: var(--spacing-sm);
  align-items: center;
  margin-bottom: var(--spacing-xs);
}

.filter-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  min-width: 200px;
}

.filter-item--wide {
  flex: 1;
  min-width: 280px;
}

.filter-item--switch {
  min-width: 160px;
}

.filter-label {
  font-size: var(--el-font-size-small);
  color: var(--el-text-color-secondary);
  white-space: nowrap;
}

.toolbar-spacer {
  flex: 1;
  min-width: 24px;
}

.toolbar-actions {
  display: flex;
  gap: var(--spacing-xs);
  flex-wrap: wrap;
}

.lookup-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: var(--el-bg-color);
  border: 1px solid var(--el-border-color-lighter);
  border-radius: var(--el-border-radius-base);
  padding: var(--spacing-md);
  min-height: 0;
  overflow-y: auto;
  box-sizing: border-box;
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: var(--spacing-md);
  width: 100%;
  box-sizing: border-box;
}

.product-card {
  position: relative;
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 16px;
  overflow: hidden;
  background-color: var(--el-bg-color);
  box-shadow: var(--el-box-shadow-light);
  transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
  display: flex;
  flex-direction: column;
  cursor: pointer;
}

.product-card:hover {
  border-color: var(--el-color-primary-light-7);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
  transform: translateY(-4px);
}

.product-card.selected-card {
  border-color: var(--el-color-primary-light-5);
  box-shadow: var(--el-box-shadow-light);
}

.product-card.banned-card {
  border-color: var(--el-color-danger-light-5);
}

.product-card.banned-card:hover {
  border-color: var(--el-color-danger);
  box-shadow: 0 6px 18px rgba(245, 108, 108, 0.2);
}

.card-checkbox {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 5;
  background-color: rgba(255, 255, 255, 0.85);
  border-radius: var(--el-border-radius-small);
  padding: 2px 4px;
}

.product-image {
  width: 100%;
  background-color: var(--el-bg-color-page);
  cursor: pointer;
  position: relative;
}

.product-image::before {
  content: '';
  display: block;
  padding-bottom: 100%;
}

.product-image img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.product-info {
  padding: var(--spacing-sm);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  flex: 1;
  min-height: 140px;
}

.product-title {
  margin: 0;
  font-size: var(--el-font-size-base);
  font-weight: var(--el-font-weight-semibold);
  color: var(--el-text-color-primary);
  line-height: 1.5;
  max-height: 3em;
  overflow: hidden;
}

.product-details {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--spacing-xs);
  flex-wrap: wrap;
}

.product-price {
  font-size: var(--el-font-size-medium);
  font-weight: var(--el-font-weight-bold);
  color: var(--el-color-primary);
}

.product-chip {
  padding: 2px 8px;
  border-radius: 999px;
  background-color: var(--el-color-info-light-9);
  color: var(--el-color-info);
  font-size: var(--el-font-size-extra-small);
}

.product-chip.warn {
  background-color: var(--el-color-warning-light-9);
  color: var(--el-color-warning);
}

.product-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: var(--el-font-size-extra-small);
  color: var(--el-text-color-secondary);
}

.product-market {
  font-weight: var(--el-font-weight-medium);
}

.product-market-copy {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  user-select: none;
}

.copy-icon {
  width: 14px;
  height: 14px;
  opacity: 0.65;
  transition: opacity 0.2s;
}

.product-market-copy:hover .copy-icon {
  opacity: 1;
}

.product-date {
  color: var(--el-text-color-placeholder);
}

.banned-list {
  font-size: var(--el-font-size-small);
  color: var(--el-color-danger);
  margin: 0;
  background-color: var(--el-color-danger-light-9);
  padding: 4px 8px;
  border-radius: var(--el-border-radius-small);
}

.product-actions {
  margin-top: auto;
}

.product-link {
  font-size: var(--el-font-size-small);
  color: var(--el-color-primary);
  text-decoration: none;
  transition: color 0.2s ease;
}

.product-link:hover {
  color: var(--el-color-primary-light-3);
  text-decoration: underline;
}

.ban-overlay {
  position: absolute;
  inset: 0;
  background-color: rgba(245, 108, 108, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: var(--spacing-sm);
  color: white;
  backdrop-filter: blur(1px);
  pointer-events: none;
}

.ban-content {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  align-items: center;
}

.ban-label {
  font-size: var(--el-font-size-large);
  font-weight: var(--el-font-weight-bold);
}

.ban-words {
  font-size: var(--el-font-size-small);
  line-height: 1.4;
}

.lookup-pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: var(--spacing-xs);
  background-color: var(--el-bg-color);
  border: 1px solid var(--el-border-color-lighter);
  border-radius: var(--el-border-radius-base);
  padding: var(--spacing-sm);
  gap: var(--spacing-md);
  flex-wrap: wrap;
}

.option-modal {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.option-summary {
  display: flex;
  justify-content: space-between;
  font-weight: var(--el-font-weight-semibold);
  font-size: var(--el-font-size-medium);
  color: var(--el-text-color-primary);
}

.option-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.option-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-sm);
  border: 1px solid var(--el-border-color-lighter);
  border-radius: var(--el-border-radius-base);
  background-color: var(--el-bg-color-page);
}

.option-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-right: var(--spacing-sm);
}

.option-title {
  font-weight: var(--el-font-weight-medium);
  color: var(--el-text-color-primary);
}

.option-desc {
  font-size: var(--el-font-size-extra-small);
  color: var(--el-text-color-secondary);
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-xs);
}

@media (max-width: 768px) {
  .lookup-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-item,
  .filter-item--wide,
  .filter-item--switch {
    width: 100%;
    min-width: 0;
    flex-direction: column;
    align-items: flex-start;
  }

  .product-image {
    height: 160px;
  }

  .lookup-pagination {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>